import useRoutes from '@/hooks/useRoutes';
import { useThemeColors } from '@/hooks/useThemeColors';
import { SectionData } from '@/types';
import { Link } from '@inertiajs/react';
import { ArrowRight, CheckCircle, ChevronDown } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface SectionHeaderProps {
    section: SectionData;
    sectionNumber?: number;
    isExpanded: boolean;
    onToggle: () => void;
    courseSlug: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
    section,
    sectionNumber,
    isExpanded,
    onToggle,
    courseSlug,
}) => {
    const { t } = useTranslation();
    const colors = useThemeColors();
    const { route } = useRoutes();

    const sectionUrl = route('section-detail', {
        slug: courseSlug,
        section_id: section.id,
    }).toString();

    const totalClasses =
        section.total_classes ??
        section.topics.reduce((total, topic) => total + topic.classes_count, 0);
    const completedClasses = section.completed_classes ?? 0;
    const isCompleted = section.is_completed ?? false;
    const evaluationsCount = section.evaluations?.length || 0;

    return (
        <div
            className={`group/header cursor-pointer border-l-4 px-6 py-4 transition-colors duration-200 hover:bg-gray-50 ${
                isCompleted
                    ? 'border-l-green-500 bg-green-50/50'
                    : `border-l-transparent hover:border-l-${colors.primary}`
            }`}
            onClick={onToggle}
        >
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <div
                        className={`relative flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                            isCompleted
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-100 text-gray-700'
                        }`}
                    >
                        {/* Brand color background, fades in on row hover */}
                        <span
                            className="absolute inset-0 rounded-full opacity-0 transition-opacity duration-300 group-hover/header:opacity-100"
                            style={{ backgroundColor: colors.progressBar }}
                        />
                        {/* Number / checkmark, fades out on row hover */}
                        <span className="relative z-10 transition-opacity duration-300 group-hover/header:opacity-0">
                            {isCompleted ? (
                                <CheckCircle size={18} />
                            ) : (
                                sectionNumber || section.order
                            )}
                        </span>
                        {/* Arrow link, fades in on row hover */}
                        <Link
                            href={sectionUrl}
                            onClick={(e) => e.stopPropagation()}
                            title={t('course.viewSectionDetail')}
                            className="absolute inset-0 z-10 flex items-center justify-center rounded-full text-white opacity-0 transition-opacity duration-300 group-hover/header:opacity-100"
                        >
                            <ArrowRight size={16} />
                        </Link>
                    </div>

                    <div className="flex-1">
                        <div className="flex items-center gap-2">
                            <h3
                                className={`text-lg font-semibold ${isCompleted ? 'text-green-800' : 'text-gray-900'}`}
                            >
                                {section.title}
                            </h3>
                            {isCompleted && (
                                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                    {t('course.completed')}
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-gray-500">
                            {section.topics.length}{' '}
                            {t('course.topics', {
                                count: section.topics.length,
                            })}{' '}
                            • {completedClasses}/{totalClasses}{' '}
                            {t('course.classes', { count: totalClasses })}
                            {evaluationsCount > 0 &&
                                ` • ${evaluationsCount} ${t('course.evaluations', { count: evaluationsCount })}`}
                        </p>
                    </div>
                </div>

                <div
                    className={`text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                >
                    <ChevronDown size={20} />
                </div>
            </div>
        </div>
    );
};

export default SectionHeader;
